import { FIELD_MAX_VOLLEYS_PER_SECOND } from '@kenocyber/game-domain';
import {
  OPEN_WORLD_PROJECTILE_RADIUS_MU,
  OPEN_WORLD_PROJECTILE_SPEED_MU_PER_TICK,
  OPEN_WORLD_TICK_MS,
} from '@kenocyber/game-domain/open-world/client';
import { OPEN_WORLD_BASE_PROJECTILE_SCALE_BPS } from '@kenocyber/shared';
import {
  PVP_CADENCE_SHOT_LENGTH,
  PVP_CADENCE_SHOT_THICKNESS,
  type PvpDashGauge,
  pvpDashGauge,
} from '../pvp/projectile-art';
import { pvpWorldUnits } from '../pvp/scene-model';
import { PVP_HULL_WORLD_UNITS } from '../pvp/ship-art';

/**
 * **O tiro primário do mundo aberto, desenhado como o jogo o desenha** (gate
 * V28I.CF.3).
 *
 * ## De onde vem a arte
 *
 * Não há arte nova aqui. O mundo aberto já desenha os cascos e os canhões com a
 * escala e as texturas aprovadas do duelo, e o tiro passa a ser o do duelo
 * também: o traço néon da caça (`ensureDashTexture`), pintado na cor do canhão
 * de quem disparou, com a bitola do duelo (`pvpDashGauge`) e o aumento de
 * cadência que o dono decidiu para os tiros de arena (`PVP_CADENCE_SHOT_*`).
 * Antes deste gate a cena desenhava um marcador provisório — um quadrado da
 * textura de reserva, a 0,09, sempre na mesma tinta ciano — que não era arte
 * nenhuma.
 *
 * ## A escala
 *
 * Um tiro primário cresce com a nave que o disparou: a sala congela a escala do
 * casco no disparo e publica-a em `sc`. O desenho multiplica **as duas**
 * dimensões do traço por essa escala, portanto o núcleo, o halo, o corpo e a
 * ponta crescem juntos — são camadas da mesma textura.
 *
 * A bitola continua medida contra o casco de REFERÊNCIA, e não contra o casco
 * que disparou: a escala entra uma vez, pelo número congelado da sala, e não
 * duas — uma pela bitola e outra pela escala.
 *
 * ## A coerência com a colisão
 *
 * O raio de colisão é o raio-base multiplicado pela MESMA escala
 * (`openWorldPrimaryProjectileBody`). A razão entre a espessura desenhada e o
 * diâmetro de colisão é portanto a mesma em todas as escalas —
 * {@link openWorldShotPaintedToHitRatio} — e é a razão aprovada no gate V27A,
 * em que o traço é mais estreito que o círculo, como sempre foi na caça.
 */

/** A escala como fator: `1` exactamente no tamanho-base. */
export function openWorldShotScaleFactor(scaleBps: number): number {
  return scaleBps === OPEN_WORLD_BASE_PROJECTILE_SCALE_BPS
    ? 1
    : scaleBps / OPEN_WORLD_BASE_PROJECTILE_SCALE_BPS;
}

/**
 * **A bitola do tiro no tamanho-base**, a mesma do duelo.
 *
 * O raio de colisão do quadrante é o do duelo, e o casco de referência também,
 * portanto a resposta é a mesma que o `PvpScene` calcula.
 */
export function openWorldShotGauge(): PvpDashGauge {
  return pvpDashGauge(pvpWorldUnits(OPEN_WORLD_PROJECTILE_RADIUS_MU), PVP_HULL_WORLD_UNITS);
}

/**
 * **A distância entre duas salvas seguidas da mesma linha de canhão**, em
 * unidades do mundo.
 *
 * `velocidade por tique × tiques por salva`, com os tiques por salva tirados da
 * cadência MÁXIMA do campo. Nem a velocidade nem a cadência crescem com o
 * casco, portanto este número é constante: hoje 62 × 1,25 = **77,5 unidades**.
 */
export function openWorldShotSpacingUnits(): number {
  return (
    pvpWorldUnits(OPEN_WORLD_PROJECTILE_SPEED_MU_PER_TICK) *
    (1_000 / FIELD_MAX_VOLLEYS_PER_SECOND / OPEN_WORLD_TICK_MS)
  );
}

/**
 * **Que fração do vão entre salvas um traço pode ocupar: metade** (gate
 * V28I.CH.11E — o visual C, aprovado pelo dono).
 *
 * A 1× o traço media 43,3 unidades em 77,5 e via-se um tiro de cada vez. A
 * escala física multiplicava-o, e a 23× eram 996,7 — treze traços sobrepostos
 * ao comprido, que é a barra branca que o dono viu na bancada. Com metade do
 * vão, dois traços seguidos deixam meio vão entre eles.
 *
 * O valor anterior aprovado era três quartos (o Visual B, `1,34×` sobre o
 * espaçamento de então); C é mais apertado, e a regressão fixa essa ordem.
 */
export const OPEN_WORLD_SHOT_LENGTH_FRACTION = 0.5;

/**
 * **O teto do comprimento desenhado, como fator sobre a bitola.**
 *
 * Derivado da velocidade autoritativa e da cadência do campo, e de mais nada:
 * sem viewport, sem zoom, sem relógio, sem cadência por corpo no protocolo.
 * Usar o PIOR caso — o TETO do campo, dez salvas por segundo desde o gate
 * V28I.CH.12 — é o que evita inventar uma mensagem só para decorar: uma nave
 * mais lenta espaça mais os tiros, logo o mesmo teto dá-lhe mais folga e nunca
 * menos. O número vem da lei e não daqui; estava escrito «16» neste comentário
 * e envelheceu com ela.
 */
function shotLengthCap(gauge: PvpDashGauge): number {
  return (
    (openWorldShotSpacingUnits() * OPEN_WORLD_SHOT_LENGTH_FRACTION) /
    (gauge.paintedLength * PVP_CADENCE_SHOT_LENGTH)
  );
}

/**
 * **A escala do sprite de um tiro primário**: bitola, aumento de cadência e
 * escala do corpo — com o COMPRIMENTO limitado e a espessura não.
 *
 * ## Porque só o comprimento
 *
 * A espessura desenhada representa o corpo, e o corpo cresce mesmo: reduzi-la
 * seria desenhar uma colisão que não existe. O comprimento não representa nada
 * físico — é o traço de movimento — e é ele que, multiplicado pela escala,
 * transformava uma sequência de tiros numa barra.
 *
 * A 23× o traço fica mais CURTO do que largo, e é por isso que o aviso de
 * extensão física de cinco tiques continua a existir e continua a usar o raio
 * de colisão: o núcleo pequeno nunca pode ser lido como "a colisão encolheu".
 *
 * ## Onde isto é chamado
 *
 * Na AQUISIÇÃO de um traço (`acquireBolt`) e no cálculo do alcance de desenho
 * (`shotReach`, memorizado por escala). Nenhuma das duas corre por quadro e por
 * corpo, e esta função não aloca nem fecha sobre nada.
 */
export function openWorldShotSpriteScale(
  gauge: PvpDashGauge,
  scaleBps: number,
): { readonly x: number; readonly y: number } {
  const factor = openWorldShotScaleFactor(scaleBps);
  const lengthFactor = Math.min(factor, shotLengthCap(gauge));
  return {
    x: gauge.scaleX * PVP_CADENCE_SHOT_LENGTH * lengthFactor,
    y: gauge.scaleY * PVP_CADENCE_SHOT_THICKNESS * factor,
  };
}

/** A espessura DESENHADA de um tiro, em unidades do mundo. */
export function openWorldShotPaintedThickness(gauge: PvpDashGauge, scaleBps: number): number {
  return gauge.paintedThickness * PVP_CADENCE_SHOT_THICKNESS * openWorldShotScaleFactor(scaleBps);
}

/** O diâmetro de COLISÃO de um tiro, em unidades do mundo, a partir do raio da sala. */
export function openWorldShotHitDiameter(bodyRadiusMu: number): number {
  return pvpWorldUnits(bodyRadiusMu) * 2;
}

/**
 * **Quanto do círculo de colisão o traço desenhado ocupa na largura.**
 *
 * Uma função da bitola e mais nada: a escala entra no numerador e no
 * denominador, e cancela. É isso que faz o desenho não mentir mais numa nave
 * grande do que numa pequena.
 */
export function openWorldShotPaintedToHitRatio(
  gauge: PvpDashGauge,
  scaleBps: number,
  bodyRadiusMu: number,
): number {
  return openWorldShotPaintedThickness(gauge, scaleBps) / openWorldShotHitDiameter(bodyRadiusMu);
}

/**
 * **O rumo de um tiro, pela velocidade que a sala publicou.**
 *
 * Um tiro primário voa a direito, portanto o rumo é o da velocidade da entrada
 * e não muda. Nunca o rumo do alvo: um tiro do mundo aberto não persegue.
 * `null` para uma velocidade nula, que não tem rumo.
 */
export function openWorldShotHeading(vx: number, vy: number): number | null {
  return vx === 0 && vy === 0 ? null : Math.atan2(vy, vx);
}

/** O que a cena desenha como tiro final: um tiro primário, e nada mais. */
export function openWorldShotDrawsAsBolt(base: {
  readonly card: unknown;
  readonly kind: 'shot' | 'bomb' | 'missile';
}): boolean {
  return base.card === null && base.kind === 'shot';
}

/**
 * **A espessura do rasto à escala do corpo**, e só a espessura.
 *
 * O comprimento, a vida e a cadência de emissão ficam os do duelo. Foi o
 * COMPRIMENTO escalado que, numa build do duelo, fez os rastos sobreporem-se em
 * linhas contínuas — o efeito-laser que o projeto proíbe —, portanto cresce só
 * o que dá a largura.
 */
export function openWorldTrailThickness(baseThickness: number, scaleBps: number): number {
  return baseThickness * openWorldShotScaleFactor(scaleBps);
}
